import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authAPI, ticketAPI } from '@/lib/api';
import { POLLING_INTERVALS, isPendingOpen } from '@/lib/constants';
import { LogOut, AlertCircle, Clock, CheckCircle2 } from 'lucide-react';
import TicketCard from '@/components/TicketCard';
import { Badge } from '@/components/ui/badge';
import InterfaceSwitcher from '@/components/InterfaceSwitcher';

const ProcessOwnerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [ticketsError, setTicketsError] = useState(false);

  const loadData = useCallback(async () => {
    // Promise.allSettled keeps /auth/me and /tickets independent. If the
    // tickets fetch fails (big payload, cold start), user still populates
    // and the InterfaceSwitcher stays mounted — so the user can always
    // switch to TC UI / Admin Panel without a page reload.
    const [userSettled, ticketsSettled] = await Promise.allSettled([
      authAPI.getMe(),
      ticketAPI.getTickets(),
    ]);
    if (userSettled.status === 'fulfilled') {
      setUser(userSettled.value.data);
    } else if (process.env.NODE_ENV !== 'production') {
      console.debug('ProcessOwnerDashboard /auth/me failed:', userSettled.reason?.message);
    }
    if (ticketsSettled.status === 'fulfilled') {
      // Hard-drop "Ticket Closed" tickets — admin-panel-only archival records.
      setTickets(
        (ticketsSettled.value.data || []).filter((t) => t.status !== 'Ticket Closed'),
      );
      setTicketsError(false);
    } else {
      // Keep the last known tickets on screen; polling retries automatically.
      setTicketsError(true);
      if (process.env.NODE_ENV !== 'production') {
        console.debug('ProcessOwnerDashboard /tickets failed:', ticketsSettled.reason?.message);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, POLLING_INTERVALS.RESOLVER_REFRESH);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'open') return isPendingOpen(ticket);
    if (filter === 'breached') return ticket.status === 'SLA BREACHED';
    if (filter === 'resolved') return ticket.status === 'Resolved';
    return true;
  });

  // "Pending & Open" only includes actionable statuses (Open, Acknowledged,
  // In Progress, SLA BREACHED, On Leave, Shift Completed, Purchase Material
  // Requested/Approved/Received, Ticket Diverted, Long Term Vacations).
  // Explicitly EXCLUDES Resolved, Closed - Feedback Received, Duplicate
  // Ticket, Completed, Ticket Closed. See lib/constants.js for the list.
  const openTickets = tickets.filter(isPendingOpen);
  const breachedTickets = tickets.filter(t => t.status === 'SLA BREACHED');
  const resolvedTickets = tickets.filter(t => t.status === 'Resolved');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-lg bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">Process Owner Dashboard</h1>
              <p className="text-sm text-slate-600">{user?.department} | {user?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <InterfaceSwitcher user={user} currentInterface="process_owner" />
              <Button 
                data-testid="logout-btn"
                variant="ghost" 
                onClick={handleLogout} 
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {ticketsError && tickets.length === 0 && (
          <div data-testid="tickets-load-error" className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">
              Tickets are taking longer than usual to load. We'll keep retrying automatically.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={loadData}
              data-testid="tickets-retry-btn"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
            >
              Retry now
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card data-testid="stats-open" className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setFilter('open')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Pending Action</p>
                  <p className="text-4xl font-bold text-yellow-600 mt-2">{openTickets.length}</p>
                </div>
                <Clock className="w-12 h-12 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stats-breached" className="bg-gradient-to-br from-red-50 to-white border-red-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setFilter('breached')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">SLA Breached</p>
                  <p className="text-4xl font-bold text-red-600 mt-2">{breachedTickets.length}</p>
                </div>
                <AlertCircle className="w-12 h-12 text-red-500 opacity-50 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stats-resolved" className="bg-gradient-to-br from-green-50 to-white border-green-200 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setFilter('resolved')}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Resolved</p>
                  <p className="text-4xl font-bold text-green-600 mt-2">{resolvedTickets.length}</p>
                </div>
                <CheckCircle2 className="w-12 h-12 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading">Department Tickets</CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  data-testid="filter-all"
                  size="sm" 
                  variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')}
                  className={filter === 'all' ? 'bg-[#E97832] hover:bg-[#C25E20]' : ''}
                >
                  All ({tickets.length})
                </Button>
                <Button 
                  data-testid="filter-open"
                  size="sm" 
                  variant={filter === 'open' ? 'default' : 'outline'}
                  onClick={() => setFilter('open')}
                  className={filter === 'open' ? 'bg-[#E97832] hover:bg-[#C25E20]' : ''}
                >
                  Open ({openTickets.length})
                </Button>
                <Button 
                  data-testid="filter-breached"
                  size="sm" 
                  variant={filter === 'breached' ? 'default' : 'outline'}
                  onClick={() => setFilter('breached')}
                  className={filter === 'breached' ? 'bg-[#E97832] hover:bg-[#C25E20]' : ''}
                >
                  Breached ({breachedTickets.length})
                </Button>
                <Button 
                  data-testid="filter-resolved"
                  size="sm" 
                  variant={filter === 'resolved' ? 'default' : 'outline'}
                  onClick={() => setFilter('resolved')}
                  className={filter === 'resolved' ? 'bg-[#E97832] hover:bg-[#C25E20]' : ''}
                >
                  Resolved ({resolvedTickets.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredTickets.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>No tickets found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTickets.map((ticket) => (
                  <TicketCard key={ticket.ticket_id} ticket={ticket} onClick={() => navigate(`/tickets/${ticket.ticket_id}`)} showActions />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProcessOwnerDashboard;
